import { useToastStore } from "@/stores/toast-store";
import { ApiError } from "./api-client";

/**
 * Deutsche Übersetzungen für gängige englische Backend-Fehlermeldungen.
 * Exakter Match wird zuerst geprüft, dann startsWith.
 */
const ERROR_TRANSLATIONS: Record<string, string> = {
	// Auth/Session
	"Invalid credentials": "Ungültige Anmeldedaten.",
	"Invalid credentials.": "Ungültige Anmeldedaten.",
	Unauthorized: "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
	"Unauthenticated.": "Deine Sitzung ist abgelaufen.",
	"Too Many Attempts.": "Zu viele Versuche. Bitte warte einen Moment.",
	"Too many requests": "Zu viele Anfragen. Bitte warte einen Moment.",

	// Validierung
	"The given data was invalid.": "Die eingegebenen Daten sind ungültig.",
	"Validation failed": "Bitte prüfe deine Eingaben.",

	// Payment
	"Insufficient balance": "Nicht genügend Guthaben.",
	"Payment required": "Bitte lade dein Konto auf.",
	"Not enough coins": "Nicht genügend Guthaben.",

	// Profil
	"Profile not found": "Profil nicht gefunden.",
	"User not found": "Benutzer nicht gefunden.",
	"Already friends": "Ihr seid bereits befreundet.",
	"Already blocked": "Dieser Benutzer ist bereits blockiert.",
	"You cannot message this user": "Du kannst diesem Benutzer keine Nachricht senden.",
	"Conversation not found": "Unterhaltung nicht gefunden.",

	// Upload
	"File too large": "Die Datei ist zu groß.",
	"The file is too large": "Die Datei ist zu groß.",
	"Invalid file type": "Ungültiger Dateityp.",

	// Server
	"Internal Server Error": "Ein interner Fehler ist aufgetreten. Bitte versuche es später erneut.",
	"Service Unavailable": "Der Dienst ist vorübergehend nicht verfügbar.",
	"Server Error": "Ein Serverfehler ist aufgetreten.",

	// Success-Messages
	success: "Erfolgreich gespeichert.",
	Success: "Erfolgreich gespeichert.",
	"Profile updated successfully": "Profil erfolgreich aktualisiert.",
	"Image updated successfully": "Bild erfolgreich aktualisiert.",
	"Password changed successfully": "Passwort erfolgreich geändert.",
	"Deleted successfully": "Erfolgreich gelöscht.",
	"Friend request sent": "Freundschaftsanfrage gesendet.",
	"Friend request accepted": "Freundschaftsanfrage angenommen.",
	"Friend request declined": "Freundschaftsanfrage abgelehnt.",
	"Friend removed": "Freund entfernt.",
	"Added to favorites": "Zu Favoriten hinzugefügt.",
	"Removed from favorites": "Aus Favoriten entfernt.",
	"User blocked": "Benutzer blockiert.",
	"User unblocked": "Benutzer entblockt.",
};

const STARTS_WITH_TRANSLATIONS: [string, string][] = [
	["The image must be a file of type", "Ungültiges Bildformat."],
	["The file must be", "Die Datei entspricht nicht den Anforderungen."],
];

export function translateMessage(message: string | undefined | null): string {
	if (!message) return "Erfolgreich gespeichert.";

	// Exakter Match
	if (ERROR_TRANSLATIONS[message]) {
		return ERROR_TRANSLATIONS[message];
	}

	// StartsWith-Match
	for (const [prefix, translation] of STARTS_WITH_TRANSLATIONS) {
		if (message.startsWith(prefix)) {
			return translation;
		}
	}

	// Kein Match -- Original zurückgeben (könnte bereits Deutsch sein)
	return message;
}

function formatValidationErrors(errors: Record<string, string[]>): string {
	const messages = Object.values(errors).flat();
	if (messages.length === 0) return "Bitte prüfe deine Eingaben.";
	if (messages.length === 1) return translateMessage(messages[0]);
	const displayed = messages.slice(0, 3).map(translateMessage);
	const remaining = messages.length - 3;
	let result = displayed.join("\n");
	if (remaining > 0) {
		result += `\n...und ${remaining} weitere Fehler`;
	}
	return result;
}

function isNetworkError(error: Error): boolean {
	return (
		error.name === "TypeError" &&
		(error.message === "Failed to fetch" ||
			error.message === "NetworkError when attempting to fetch resource." ||
			error.message === "Load failed")
	);
}

/**
 * Globaler Mutation-Error-Handler. Wird aus MutationCache.onError aufgerufen.
 * Zeigt einen Error-Toast, AUSSER:
 * - Die Mutation hat meta.skipGlobalErrorHandler gesetzt
 * - Die Mutation hat einen eigenen onError-Handler
 * - Es ist ein 401 (wird separat behandelt)
 */
export function handleMutationError(
	error: Error,
	_variables: unknown,
	_context: unknown,
	mutation: { meta?: Record<string, unknown>; options: { onError?: unknown } },
): void {
	// Skip wenn meta-Flag gesetzt
	if (mutation.meta?.skipGlobalErrorHandler) return;

	// Skip wenn die Mutation einen eigenen onError hat
	if (mutation.options.onError) return;

	// 401 wird vom bestehenden Session-Handler behandelt
	if (error instanceof ApiError && error.status === 401) return;

	const addToast = useToastStore.getState().addToast;

	// Netzwerkfehler
	if (isNetworkError(error)) {
		addToast({
			type: "error",
			message: "Verbindung fehlgeschlagen. Bitte prüfe deine Internetverbindung.",
		});
		return;
	}

	if (error instanceof ApiError) {
		// 413 kommt von nginx oder Laravels ValidatePostSize und traegt keinen
		// verwertbaren Body -- ohne diesen Zweig landet der Nutzer beim generischen
		// Text, obwohl die Ursache eindeutig ist.
		if (error.status === 413) {
			addToast({
				type: "error",
				message: "Die Datei ist zu groß. Bitte wähle ein kleineres Bild.",
			});
			return;
		}

		// 422 Validierungsfehler mit Feld-Details
		if (error.status === 422 && error.errors && typeof error.errors === "object") {
			addToast({
				type: "error",
				message: formatValidationErrors(error.errors as Record<string, string[]>),
			});
			return;
		}

		// Andere ApiError: Message übersetzen
		addToast({
			type: "error",
			message: translateMessage(error.message),
		});
		return;
	}

	// Generischer Fallback
	addToast({
		type: "error",
		message: "Ein unerwarteter Fehler ist aufgetreten.",
	});
}
